import { useState } from "react";

import {
  Alert,
  Modal,
  TextFieldInput,
} from "@smart/itops-sb-design-system-dom";

import { duplicateNameMessage, isDuplicateName } from "./customise-helpers";
import { getDefaultContactDetailsLabel } from "../../utils";

export type SaveFieldSetModalProps = {
  roleDisplayName: string;
  existingNames?: string[];
  headerText?: string;
  loading?: boolean;
  error?: string;
  onCancel: () => void;
  onSave: (name: string) => void | Promise<void>;
};

export const SaveFieldSetModal = ({
  roleDisplayName,
  existingNames = [],
  headerText,
  loading,
  error,
  onCancel,
  onSave,
}: SaveFieldSetModalProps) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string>();

  const trimmed = name.trim();
  const canSave = !!trimmed && !loading;

  const onSaveClick = () => {
    if (isDuplicateName({ trimmed, existingNames })) {
      setNameError(duplicateNameMessage);
      return undefined;
    }

    return onSave(trimmed);
  };

  return (
    <Modal
      open
      onClose={loading ? undefined : onCancel}
      loading={loading}
      header={{ text: headerText }}
      footer={{
        buttons: [
          {
            text: "Cancel",
            variant: "secondarySubtle",
            disabled: loading,
            onClick: onCancel,
          },
          {
            text: "Save field set",
            variant: "primaryDefault",
            disabled: !canSave,
            onClick: onSaveClick,
          },
        ],
      }}
    >
      <div className="flex flex-col gap-medium">
        <p className="m-0 text-ui text-neutral-500">
          Reuse this contact field set on any future intake form.
        </p>
        <TextFieldInput
          id="field-set-name"
          label="Field set name"
          mandatory
          value={name}
          placeholder={getDefaultContactDetailsLabel(roleDisplayName)}
          onChange={(e) => {
            setNameError(undefined);
            setName(e.target.value);
          }}
          disabled={loading}
          error={!!nameError}
          message={nameError}
        />
        <Alert
          variant="section"
          palette="default"
          content={
            <>
              Field sets appear under{" "}
              <b>Add contact details → Your field sets</b> on this and future
              intake forms, ready to drop in.
            </>
          }
        />
        {error && (
          <Alert variant="section" palette="destructive" content={error} />
        )}
      </div>
    </Modal>
  );
};
